import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import GroupIcon from "@mui/icons-material/GroupOutlined";
import EventNoteIcon from "@mui/icons-material/EventNoteOutlined";
import Swal from "sweetalert2";
import StatusChip from "components/StatusChip";
import { API_BASE } from "config/api";

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const TeamCell = ({ row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {row.original.name}
    </Typography>
    {row.original.description && (
      <Typography variant="caption" color="text.secondary">
        {row.original.description}
      </Typography>
    )}
  </Box>
);
TeamCell.propTypes = { row: PropTypes.shape({ original: PropTypes.object }).isRequired };

const LeaderCell = ({ value }) => (
  <Typography variant="button" fontWeight={500}>
    {value || "—"}
  </Typography>
);
LeaderCell.propTypes = { value: PropTypes.string };
LeaderCell.defaultProps = { value: "" };

const StatusCell = ({ value }) => <StatusChip value={value} />;
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const ActionsCell = ({ row, handleEdit, handleDelete, handleMembers, handleSchedules }) => (
  <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
    <Button
      size="small"
      variant="outlined"
      color="info"
      startIcon={<GroupIcon fontSize="small" />}
      onClick={() => handleMembers(row.original)}
    >
      Members ({row.original.memberCount})
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="success"
      startIcon={<EventNoteIcon fontSize="small" />}
      onClick={() => handleSchedules(row.original)}
    >
      Rosters
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Stack>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleMembers: PropTypes.func.isRequired,
  handleSchedules: PropTypes.func.isRequired,
};

const emptyForm = () => ({
  id: null,
  name: "",
  description: "",
  leader_member_id: null,
  leaderLabel: "",
  status: "active",
});

const emptyScheduleForm = () => ({
  title: "",
  service_date: new Date().toISOString().split("T")[0],
  service_time: "",
  notes: "",
});

export default function useServingTeamData() {
  const { church } = useAuth();

  const [teamData, setTeamData] = useState({ rows: [], isLoading: true, error: null });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [schedulesModalOpen, setSchedulesModalOpen] = useState(false);
  const [assignmentsModalOpen, setAssignmentsModalOpen] = useState(false);

  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [isSaving, setIsSaving] = useState(false);

  // Leader search (team form)
  const [leaderSearch, setLeaderSearch] = useState("");
  const [leaderResults, setLeaderResults] = useState([]);

  // Members modal
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [memberResults, setMemberResults] = useState([]);
  const [memberRoleLabel, setMemberRoleLabel] = useState("");

  // Schedules modal
  const [schedules, setSchedules] = useState([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);
  const [scheduleForm, setScheduleForm] = useState(emptyScheduleForm());
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);

  // Assignments modal
  const [assignmentSearch, setAssignmentSearch] = useState("");
  const [assignmentResults, setAssignmentResults] = useState([]);
  const [isSendingReminders, setIsSendingReminders] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem("authToken");
    return token ? { Accept: "application/json", Authorization: `Bearer ${token}` } : null;
  };

  const fetchTeams = useCallback(async () => {
    if (!church?.id) return;
    setTeamData((prev) => ({ ...prev, isLoading: true }));
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/serving-teams`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const teams = result.data || [];
      setTeamData({
        rows: teams.map((t, index) => ({
          id: t.id,
          serial: index + 1,
          name: t.name,
          description: t.description,
          leaderName: t.leader ? `${t.leader.first_name} ${t.leader.surname}` : "",
          status: t.status,
          memberCount: t.members_count || 0,
          originalData: t,
        })),
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error("Fetch serving teams failed:", error);
      setTeamData((prev) => ({ ...prev, isLoading: false, error: error.message }));
    }
  }, [church?.id]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleEdit = useCallback((team) => {
    const raw = team.originalData || {};
    const leaderLabel = raw.leader ? `${raw.leader.first_name} ${raw.leader.surname}` : "";
    setForm({
      id: team.id,
      name: raw.name || "",
      description: raw.description || "",
      leader_member_id: raw.leader_member_id || null,
      leaderLabel,
      status: raw.status || "active",
    });
    setLeaderSearch(leaderLabel);
    setLeaderResults([]);
    setEditModalOpen(true);
  }, []);

  const handleAdd = () => {
    setForm(emptyForm());
    setLeaderSearch("");
    setLeaderResults([]);
    setEditModalOpen(true);
  };

  const handleDelete = useCallback((team) => {
    setSelectedTeam(team);
    setDeleteModalOpen(true);
  }, []);

  const searchMembers = async (query, setResults) => {
    if (!church?.id || !query.trim()) {
      setResults([]);
      return;
    }
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(
        `${API_BASE}/members/${church.id}/search?query=${encodeURIComponent(query)}`,
        { headers }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setResults(result.data || []);
    } catch (error) {
      console.error("Member search failed:", error);
      setResults([]);
    }
  };

  useEffect(() => {
    if (!leaderSearch.trim() || leaderSearch === form.leaderLabel) {
      setLeaderResults([]);
      return;
    }
    const timer = setTimeout(() => searchMembers(leaderSearch, setLeaderResults), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [leaderSearch]);

  const handleSelectLeader = (member) => {
    const label = `${member.first_name} ${member.surname}`;
    setForm((prev) => ({ ...prev, leader_member_id: member.id, leaderLabel: label }));
    setLeaderSearch(label);
    setLeaderResults([]);
  };

  const handleSubmit = async () => {
    try {
      if (!form.name.trim()) throw new Error("Team name is required.");
      setIsSaving(true);
      const headers = authHeaders();
      if (!headers) return;

      const url = form.id ? `${API_BASE}/serving-teams/${form.id}` : `${API_BASE}/serving-teams`;
      const method = form.id ? "PUT" : "POST";

      const payload = {
        name: form.name,
        description: form.description || null,
        leader_member_id: form.leader_member_id || null,
        status: form.status,
      };

      const response = await fetch(url, {
        method,
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Team ${form.id ? "updated" : "created"}`,
        icon: "success",
      });
      fetchTeams();
      setEditModalOpen(false);
    } catch (error) {
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/serving-teams/${selectedTeam.id}`, {
        method: "DELETE",
        headers,
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      Swal.fire({ title: "Success!", text: "Team deleted", icon: "success" });
      fetchTeams();
      setDeleteModalOpen(false);
    } catch (error) {
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  // ---- Members modal ----

  const fetchTeamMembers = useCallback(async (teamId) => {
    setIsLoadingMembers(true);
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/serving-teams/${teamId}/members`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setTeamMembers(result.data || []);
    } catch (error) {
      console.error("Fetch team members failed:", error);
    } finally {
      setIsLoadingMembers(false);
    }
  }, []);

  const handleMembers = useCallback(
    (team) => {
      setSelectedTeam(team);
      setMemberSearch("");
      setMemberResults([]);
      setMemberRoleLabel("");
      fetchTeamMembers(team.id);
      setMembersModalOpen(true);
    },
    [fetchTeamMembers]
  );

  useEffect(() => {
    if (!memberSearch.trim()) {
      setMemberResults([]);
      return;
    }
    const timer = setTimeout(() => searchMembers(memberSearch, setMemberResults), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [memberSearch]);

  const handleAddTeamMember = async (member) => {
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/serving-teams/${selectedTeam.id}/members`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ member_id: member.id, role_label: memberRoleLabel || null }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }
      setMemberSearch("");
      setMemberResults([]);
      setMemberRoleLabel("");
      fetchTeamMembers(selectedTeam.id);
      fetchTeams();
    } catch (error) {
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  const handleRemoveTeamMember = async (memberId) => {
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(
        `${API_BASE}/serving-teams/${selectedTeam.id}/members/${memberId}`,
        { method: "DELETE", headers }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      fetchTeamMembers(selectedTeam.id);
      fetchTeams();
    } catch (error) {
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  // ---- Schedules modal ----

  const fetchSchedules = useCallback(async (teamId) => {
    setIsLoadingSchedules(true);
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/serving-teams/${teamId}/schedules`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setSchedules(result.data || []);
    } catch (error) {
      console.error("Fetch schedules failed:", error);
    } finally {
      setIsLoadingSchedules(false);
    }
  }, []);

  const handleSchedules = useCallback(
    (team) => {
      setSelectedTeam(team);
      setScheduleForm(emptyScheduleForm());
      fetchSchedules(team.id);
      setSchedulesModalOpen(true);
    },
    [fetchSchedules]
  );

  const handleCreateSchedule = async () => {
    try {
      if (!scheduleForm.title.trim()) throw new Error("Title is required.");
      if (!scheduleForm.service_date) throw new Error("Service date is required.");
      setIsSavingSchedule(true);
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/serving-teams/${selectedTeam.id}/schedules`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          title: scheduleForm.title,
          service_date: scheduleForm.service_date,
          service_time: scheduleForm.service_time || null,
          notes: scheduleForm.notes || null,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }
      Swal.fire({ title: "Success!", text: "Roster schedule created", icon: "success" });
      setScheduleForm(emptyScheduleForm());
      fetchSchedules(selectedTeam.id);
    } catch (error) {
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(
        `${API_BASE}/serving-teams/${selectedTeam.id}/schedules/${scheduleId}`,
        { method: "DELETE", headers }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      fetchSchedules(selectedTeam.id);
    } catch (error) {
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  // ---- Assignments modal ----

  const handleOpenAssignments = (schedule) => {
    setSelectedSchedule(schedule);
    setAssignmentSearch("");
    setAssignmentResults([]);
    setAssignmentsModalOpen(true);
  };

  useEffect(() => {
    if (!assignmentSearch.trim()) {
      setAssignmentResults([]);
      return;
    }
    const timer = setTimeout(() => searchMembers(assignmentSearch, setAssignmentResults), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [assignmentSearch]);

  const handleAddAssignment = async (member) => {
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(
        `${API_BASE}/serving-teams/${selectedTeam.id}/schedules/${selectedSchedule.id}/assignments`,
        {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ member_id: member.id }),
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }
      setAssignmentSearch("");
      setAssignmentResults([]);
      fetchSchedules(selectedTeam.id);
      const updated = await response.json();
      setSelectedSchedule((prev) => ({
        ...prev,
        assignments: [...(prev.assignments || []), updated.data],
      }));
    } catch (error) {
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(
        `${API_BASE}/serving-teams/${selectedTeam.id}/schedules/${selectedSchedule.id}/assignments/${assignmentId}`,
        { method: "DELETE", headers }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      setSelectedSchedule((prev) => ({
        ...prev,
        assignments: (prev.assignments || []).filter((a) => a.id !== assignmentId),
      }));
      fetchSchedules(selectedTeam.id);
    } catch (error) {
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  const handleSendReminders = async () => {
    try {
      setIsSendingReminders(true);
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(
        `${API_BASE}/serving-teams/${selectedTeam.id}/schedules/${selectedSchedule.id}/remind`,
        { method: "POST", headers }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      Swal.fire({
        title: "Reminders sent!",
        text: `${result.data?.sent || 0} member(s) notified by SMS.`,
        icon: "success",
      });
      fetchSchedules(selectedTeam.id);
    } catch (error) {
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    } finally {
      setIsSendingReminders(false);
    }
  };

  const columns = [
    { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
    { Header: "Team", accessor: "name", align: "left", width: "25%", Cell: TeamCell },
    { Header: "Leader", accessor: "leaderName", align: "left", width: "15%", Cell: LeaderCell },
    { Header: "Status", accessor: "status", align: "center", width: "10%", Cell: StatusCell },
    {
      Header: "Actions",
      accessor: "actions",
      align: "center",
      width: "45%",
      Cell: (props) => (
        <ActionsCell
          {...props}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleMembers={handleMembers}
          handleSchedules={handleSchedules}
        />
      ),
    },
  ];

  return {
    columns,
    rows: teamData.rows,
    isLoading: teamData.isLoading,
    error: teamData.error,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    membersModalOpen,
    setMembersModalOpen,
    schedulesModalOpen,
    setSchedulesModalOpen,
    assignmentsModalOpen,
    setAssignmentsModalOpen,
    selectedTeam,
    selectedSchedule,
    form,
    setForm,
    handleAdd,
    handleSubmit,
    handleDeleteConfirm,
    isSaving,
    leaderSearch,
    setLeaderSearch,
    leaderResults,
    handleSelectLeader,
    teamMembers,
    isLoadingMembers,
    memberSearch,
    setMemberSearch,
    memberResults,
    memberRoleLabel,
    setMemberRoleLabel,
    handleAddTeamMember,
    handleRemoveTeamMember,
    schedules,
    isLoadingSchedules,
    scheduleForm,
    setScheduleForm,
    isSavingSchedule,
    handleCreateSchedule,
    handleDeleteSchedule,
    handleOpenAssignments,
    assignmentSearch,
    setAssignmentSearch,
    assignmentResults,
    handleAddAssignment,
    handleRemoveAssignment,
    isSendingReminders,
    handleSendReminders,
  };
}
